import { ReactElement, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import thumbsUpSvg from 'assets/icons/thumbs_up.svg';
import thumbsDownSvg from 'assets/icons/thumbs_down.svg';
import hourglassSvg from 'assets/icons/hourglass.svg';
import checkmarkSvg from 'assets/icons/check.svg';
import crossSvg from 'assets/icons/close.svg';
import removeSvg from 'assets/icons/remove.svg';
import arrowRightSvg from 'assets/icons/arrow_right.svg';

import { editCreativesDeleteSelected, editCreativesStatusChange } from 'modules/api/bulkEditCreatives';
import { cancelFunctions } from 'utils/cancelFunctions';
import { usePageFilters } from 'customHooks/usePageFilters';
import { addToastNotification } from 'store/notification/reducer';
import { updateCreativesStatus } from 'store/pages/contentManagement/creatives/reducer';
import { Store } from 'components/common/types/Store.types';
import {
  getAllSelectedCreativesPendingStatus,
  getAllSelectedCreativesPendingAndOrRejected,
  getAllSelectedCreativeHaveDefinedDealId,
} from 'store/pages/contentManagement/creatives/selectors';
import Button, { Size, Color } from 'lib/Button';
import Modal, { ModalSize } from 'lib/Modal';
import Input from 'lib/Input';
import SingleContent from 'components/common/layout/SingleContent';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { getIsReseller } from 'store/publisher/selectors';
import cx from 'classnames';
import { ActionsPanelProps } from './ActionsPanel.types';
import { checkResponseResultErrors, getCreativeMarketId } from '../utils';

const ActionsPanel: React.FC<ActionsPanelProps> = ({
  formMetadata,
  isFormTouched,
  onSaveButtonClick,
  onClearButtonClick,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const DEFAULT_REJECTION_REASON = 'Batch Rejection.';

  const [isLoading, setIsLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState(DEFAULT_REJECTION_REASON);
  const [isRejectModal, setIsRejectModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const areAllItemsPending = useSelector(getAllSelectedCreativesPendingStatus);
  const areAllItemsPendingAndOrRejected = useSelector(getAllSelectedCreativesPendingAndOrRejected);
  const isReseller = useSelector(getIsReseller);
  const selectedCreatives = useSelector((state: Store) => state.pages.contentManagement.creatives.selectedCreatives);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { changeFilters } = usePageFilters('content-management/creatives');
  const areAllItemsAssignedToDealId = useSelector(getAllSelectedCreativeHaveDefinedDealId);

  const hasDealId = formMetadata.dealId.length > 0;

  const onChangeCreativesStatus = async (statusCode: CreativeStatus, rejectionReason?: string): Promise<void> => {
    setIsLoading(true);

    try {
      checkResponseResultErrors(
        await editCreativesStatusChange(cancelFunctions, {
          status: statusCode,
          ids: selectedCreatives.map(getCreativeMarketId),
          rejectionReason,
        }),
      );
      dispatch(updateCreativesStatus(statusCode));

      if (statusCode === CreativeStatus.REJECTED) {
        setIsRejectModal(false);
        setRejectReason(DEFAULT_REJECTION_REASON);
      }
      // eslint-disable-next-line no-empty
    } catch {}

    setIsLoading(false);
  };

  const onDeleteCreatives = async (): Promise<void> => {
    setIsLoading(true);

    try {
      checkResponseResultErrors(
        await editCreativesDeleteSelected(cancelFunctions, {
          ids: selectedCreatives.map(getCreativeMarketId),
        }),
      );

      navigate('/content-management/creatives');
      changeFilters({ deleted: true });
      dispatch(addToastNotification({ description: 'Creatives deleted Successfully', timeout: 2000, type: 'Success' }));
      // eslint-disable-next-line no-empty
    } catch {}

    setIsLoading(false);
  };

  const onShowRejectModal = (): void => setIsRejectModal(true);
  const onHideRejectModal = (): void => {
    setIsRejectModal(false);
    setRejectReason(DEFAULT_REJECTION_REASON);
  };

  const onHandleRejectReason = (userInput: string): void => setRejectReason(userInput);

  const areAllRequiredFormMetadataAssigned = useMemo(() => {
    const requiredFormMetaData = {
      advertiser: formMetadata.advertiser,
      brand: formMetadata.brand,
    };

    return !!formMetadata && Object.values(requiredFormMetaData).every(Boolean);
  }, [formMetadata]);

  const areAllSelectedCreativesWithRequiredMetadata = selectedCreatives.every(
    ({ advertiser, brand }) => advertiser && brand,
  );

  const isSaveButtonDisabled = isLoading || !!(formMetadata?.advertiser && !formMetadata?.brand);

  const rejectButton = (): ReactElement => (
    <Button
      dataTestId="reject-button"
      size={Size.LARGE}
      color={Color.DANGER}
      onClick={onShowRejectModal}
      isDisabled={isLoading}
      label="Reject"
      svg={thumbsDownSvg}
    />
  );

  const pendingButton = (): ReactElement => (
    <Button
      dataTestId="pending-button"
      size={Size.LARGE}
      color={Color.YELLOW}
      onClick={() => onChangeCreativesStatus(CreativeStatus.PENDING)}
      isDisabled={isLoading}
      label="Pending"
      svg={hourglassSvg}
    />
  );

  const deleteButton = (): ReactElement => (
    <Button
      onClick={() => setShowDeleteDialog(true)}
      size={Size.LARGE}
      color={Color.PRIMARY}
      label="Delete"
      svg={removeSvg}
    />
  );

  const approveButton = (): ReactElement => (
    <Button
      dataTestId="approve-button"
      size={Size.LARGE}
      color={Color.GREEN}
      isDisabled={(!areAllRequiredFormMetadataAssigned && !areAllSelectedCreativesWithRequiredMetadata) || isLoading}
      onClick={() => onChangeCreativesStatus(CreativeStatus.APPROVED)}
      label="Approve"
      svg={thumbsUpSvg}
    />
  );

  const sendForApprovalButton = (): ReactElement => (
    <Button
      onClick={() => onChangeCreativesStatus(CreativeStatus.SENT_FOR_APPROVAL)}
      size={Size.LARGE}
      color={Color.PRIMARY}
      dataTestId="creative-status-send-for-approval-btn"
      isDisabled={!hasDealId && !areAllItemsAssignedToDealId}
      dataPendoId="/content-management/edit-creatives | Send for approval"
      svg={arrowRightSvg}
      label="Send for approval"
    />
  );

  const resellerActionButtons = (): ReactElement | false =>
    areAllItemsPending && (
      <>
        {sendForApprovalButton()}
        {rejectButton()}
      </>
    );
  const publisherActionButtons = (): ReactElement => (
    <>
      {areAllItemsPending && rejectButton()}
      {!areAllItemsPending && pendingButton()}
      {areAllItemsPending && approveButton()}
    </>
  );

  return (
    <div
      className={cx('border-t border-neutral-900-opacity-10 bg-neutral-50 absolute w-full left-0 bottom-1.5', {
        'h-16': !isReseller,
        'h-44': isReseller,
      })}
    >
      <SingleContent
        content={
          <>
            <div className={cx('flex gap-x-3 h-full', { 'flex-col w-full gap-y-2': isReseller })}>
              {!isFormTouched ? (
                <>
                  {isReseller ? resellerActionButtons() : publisherActionButtons()}
                  {areAllItemsPendingAndOrRejected && deleteButton()}
                </>
              ) : (
                <>
                  <Button
                    dataTestId="clear-button"
                    size={Size.LARGE}
                    color={Color.BLACK_AND_WHITE}
                    onClick={onClearButtonClick}
                    label="Clear"
                    svg={crossSvg}
                  />
                  <Button
                    dataTestId="save-button"
                    size={Size.LARGE}
                    color={Color.SECONDARY}
                    isDisabled={isSaveButtonDisabled}
                    onClick={onSaveButtonClick}
                    label="Save"
                    svg={checkmarkSvg}
                  />
                </>
              )}
            </div>

            <Modal
              dataTestId="rejection-modal"
              isOpen={isRejectModal}
              icon={crossSvg}
              title="Reject creative?"
              size={ModalSize.SMALL}
              onClose={onHideRejectModal}
            >
              <Input placeholder="Reject reason..." value={rejectReason} onChange={onHandleRejectReason} />
              <div className="flex justify-end mt-4 space-x-4">
                <Button
                  dataTestId="hide-reject-modal-button"
                  size={Size.LARGE}
                  color={Color.PRIMARY}
                  onClick={onHideRejectModal}
                  label="Cancel"
                />
                <Button
                  dataTestId="reject-modal-button"
                  size={Size.LARGE}
                  color={Color.DANGER}
                  onClick={() => onChangeCreativesStatus(CreativeStatus.REJECTED, rejectReason)}
                  isDisabled={rejectReason.length < 3}
                  label="Reject"
                />
              </div>
            </Modal>

            <Modal
              dataTestId="delete-creatives-modal"
              isOpen={showDeleteDialog}
              icon={removeSvg}
              title="Delete Creatives?"
              actionButtons={
                <>
                  <Button
                    color={Color.PRIMARY}
                    size={Size.LARGE}
                    onClick={() => setShowDeleteDialog(false)}
                    label="Cancel"
                  />
                  <Button color={Color.DANGER} size={Size.LARGE} onClick={onDeleteCreatives} label="Delete" />
                </>
              }
            >
              <p className="ml-8 body-base text-neutral-950-opacity-60">
                Are you sure you want to delete these creatives?
              </p>
            </Modal>
          </>
        }
      />
    </div>
  );
};

export default ActionsPanel;
